import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import { clientConfigService } from "../../utils/services/clientConfigService";
import { analyticsWarehouseService } from "../../utils/services/analyticsWarehouseService";
import { wmsService } from "../../utils/services/wmsProvider";
import {
  normalizeWarehouseSelection,
  warehouseOptionLabel,
} from "../../utils/analyticsWarehouses";
import SettingsSection, {
  SectionAction,
  SectionFooter,
  settingsButtonSx,
  settingsText,
} from "../settings-section";

export const ANALYTICS_WAREHOUSES_KEY = "analytics_warehouse_ids";
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;
const MAX_SELECTION = 100;

const helperSx = { ...settingsText.muted, lineHeight: 1.5 };
const valueSx = settingsText.value;

const toOption = (warehouse) => ({
  _id: String(warehouse._id),
  name: warehouse.name || null,
  warehouse_code: warehouse.warehouse_code || null,
});

/**
 * Client Config card for `analytics_warehouse_ids`: which warehouses the
 * analytics warehouse selectors offer for this client. Stores warehouse ids;
 * shows "Name (code)". Empty = every warehouse (no restriction). Selecting
 * here never changes warehouses themselves or operational screens.
 */
const AnalyticsWarehousesSetting = ({ config, clientId }) => {
  // Saved state
  const [row, setRow] = useState(null);
  const [saved, setSaved] = useState([]);
  const [missingCount, setMissingCount] = useState(0);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [savedError, setSavedError] = useState("");

  // Edit state
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [total, setTotal] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState("");
  const requestRef = useRef(0);

  const loadSaved = useCallback(async () => {
    if (!clientId) return;
    setLoadingSaved(true);
    setSavedError("");
    try {
      const configRes = await clientConfigService.fetch(
        clientId,
        1,
        0,
        ANALYTICS_WAREHOUSES_KEY,
      );
      const current = (configRes?.data || [])[0] || null;
      const ids = Array.isArray(current?.value) ? current.value.map(String) : [];
      let resolved = [];
      if (ids.length) {
        // The analytics options endpoint returns exactly the configured
        // warehouses that still exist, with their names and codes.
        const optionsRes = await analyticsWarehouseService.fetch({
          clientId,
          limit: MAX_SELECTION,
        });
        const wanted = new Set(ids);
        resolved = (optionsRes?.data || [])
          .filter((w) => wanted.has(String(w._id)))
          .map(toOption);
      }
      setRow(current);
      setSaved(normalizeWarehouseSelection(resolved));
      setMissingCount(Math.max(0, ids.length - resolved.length));
    } catch (error) {
      setSavedError("Couldn't load the analytics warehouse setting.");
    } finally {
      setLoadingSaved(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadSaved();
  }, [loadSaved]);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // All of the client's warehouses (operational list, read-only) so admins
  // can choose from every location, searchable and paged server-side.
  const loadOptions = useCallback(
    async (skip) => {
      const requestId = ++requestRef.current;
      setOptionsLoading(true);
      setOptionsError("");
      try {
        const res = await wmsService.fetch(clientId, PAGE_SIZE, skip, undefined, search);
        if (requestId !== requestRef.current) return;
        const rows = (res?.data || []).filter((w) => w?._id).map(toOption);
        setOptions((prev) => (skip ? [...prev, ...rows] : rows));
        setTotal(Number.isFinite(res?.total) ? res.total : rows.length);
      } catch (error) {
        if (requestId !== requestRef.current) return;
        setOptionsError("Couldn't load warehouses.");
      } finally {
        if (requestId === requestRef.current) setOptionsLoading(false);
      }
    },
    [clientId, search],
  );

  useEffect(() => {
    if (editing) loadOptions(0);
  }, [editing, loadOptions]);

  const draftIds = useMemo(() => new Set(draft.map((w) => w._id)), [draft]);
  const hasMore = total != null && options.length < total;
  const dirty = useMemo(() => {
    const a = draft.map((w) => w._id).sort().join(",");
    const b = saved.map((w) => w._id).sort().join(",");
    return a !== b || missingCount > 0;
  }, [draft, saved, missingCount]);

  const startEditing = () => {
    setDraft(saved);
    setSearchInput("");
    setSearch("");
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraft([]);
    setOptions([]);
    setTotal(null);
  };

  const toggle = (option) => {
    setDraft((prev) =>
      draftIds.has(option._id)
        ? prev.filter((w) => w._id !== option._id)
        : normalizeWarehouseSelection([...prev, option]),
    );
  };

  const selectAllShown = () =>
    setDraft((prev) =>
      normalizeWarehouseSelection([...prev, ...options]).slice(0, MAX_SELECTION),
    );

  const onListScroll = (event) => {
    const el = event.currentTarget;
    if (!optionsLoading && hasMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      loadOptions(options.length);
    }
  };

  const save = async () => {
    if (draft.length > MAX_SELECTION) {
      toast.error(`Select at most ${MAX_SELECTION} warehouses.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        key: ANALYTICS_WAREHOUSES_KEY,
        value: draft.map((w) => w._id).sort(),
      };
      if (row?._id) await clientConfigService.edit(row._id, payload);
      else await clientConfigService.create(payload);
      toast.success(
        draft.length
          ? "Analytics warehouses saved."
          : "Analytics warehouses cleared — all warehouses will be offered.",
      );
      setEditing(false);
      await loadSaved();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Couldn't save analytics warehouses.",
      );
    } finally {
      setSaving(false);
    }
  };

  const renderChips = (list, { onDelete } = {}) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {list.map((w) => (
        <Chip
          key={w._id}
          size="small"
          variant="outlined"
          label={warehouseOptionLabel(w)}
          onDelete={onDelete ? () => onDelete(w) : undefined}
          sx={{
            fontFamily: "poppins",
            fontSize: 12.5,
            maxWidth: "100%",
            borderColor: "var(--border-color)",
            color: "var(--text-primary)",
          }}
        />
      ))}
    </Box>
  );

  const renderView = () => {
    if (loadingSaved) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} />
          <Typography sx={valueSx}>Loading…</Typography>
        </Box>
      );
    }
    if (savedError) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={valueSx}>{savedError}</Typography>
          <Button size="small" onClick={loadSaved} sx={{ textTransform: "none" }}>
            Retry
          </Button>
        </Box>
      );
    }
    if (!saved.length) {
      return (
        <Box>
          <Typography sx={{ ...valueSx, fontWeight: 500 }}>
            All warehouses
          </Typography>
          <Typography sx={{ ...helperSx, mt: 0.25 }}>
            No restriction — analytics selectors offer every warehouse.
          </Typography>
          {missingCount > 0 && (
            <Typography sx={{ ...helperSx, mt: 0.25 }}>
              {missingCount} previously selected warehouse
              {missingCount === 1 ? " no longer exists" : "s no longer exist"}.
            </Typography>
          )}
        </Box>
      );
    }
    return (
      <Box>
        <Typography sx={{ ...valueSx, fontWeight: 500, mb: 1.25 }}>
          {saved.length} warehouse{saved.length === 1 ? "" : "s"} offered in analytics
        </Typography>
        {renderChips(saved)}
        {missingCount > 0 && (
          <Typography sx={{ ...helperSx, mt: 1 }}>
            {missingCount} configured warehouse
            {missingCount === 1 ? " no longer exists and is" : "s no longer exist and are"}{" "}
            skipped. Saving again removes {missingCount === 1 ? "it" : "them"}.
          </Typography>
        )}
      </Box>
    );
  };

  const renderEditor = () => (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography sx={{ ...valueSx, fontSize: 13 }}>
          {draft.length ? `${draft.length} selected` : "None selected"}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button
            size="small"
            onClick={selectAllShown}
            disabled={!options.length || saving}
            sx={{ textTransform: "none", fontFamily: "poppins", fontSize: 12.5 }}
          >
            Select all shown
          </Button>
          <Button
            size="small"
            onClick={() => setDraft([])}
            disabled={!draft.length || saving}
            sx={{ textTransform: "none", fontFamily: "poppins", fontSize: 12.5 }}
          >
            Clear all
          </Button>
        </Box>
      </Box>

      {draft.length > 0 ? (
        <Box
          sx={{ maxHeight: 112, overflowY: "auto", mb: 1.5 }}
          data-testid="analytics-warehouses-selected"
        >
          {renderChips(draft, { onDelete: toggle })}
        </Box>
      ) : (
        <Alert
          severity="info"
          sx={{ mb: 1.5, py: 0, fontFamily: "poppins", fontSize: 12.5, textAlign: "left" }}
        >
          With no warehouses selected, analytics selectors offer every warehouse.
        </Alert>
      )}

      <Box sx={{ border: "1px solid var(--border-color)", borderRadius: 1.5 }}>
        <Box sx={{ p: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name or code"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            inputProps={{ "aria-label": "Search warehouses" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "var(--text-tertiary)" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <Box
          role="listbox"
          aria-multiselectable="true"
          aria-label="Warehouses"
          onScroll={onListScroll}
          sx={{ maxHeight: 280, overflowY: "auto", py: 0.5 }}
        >
          {options.map((option) => {
            const checked = draftIds.has(option._id);
            return (
              <MenuItem
                key={option._id}
                dense
                role="option"
                aria-selected={checked}
                onClick={() => toggle(option)}
                disabled={saving}
                sx={{ py: 0.25 }}
              >
                <Checkbox size="small" checked={checked} sx={{ p: 0.5, mr: 1 }} />
                <ListItemText
                  primary={option.name || option.warehouse_code}
                  secondary={option.name && option.warehouse_code ? option.warehouse_code : null}
                  primaryTypographyProps={{ fontFamily: "poppins", fontSize: 13.5 }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                />
              </MenuItem>
            );
          })}
          {optionsLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25 }}>
              <CircularProgress size={14} />
              <Typography sx={{ ...valueSx, fontSize: 12.5 }}>Loading warehouses…</Typography>
            </Box>
          )}
          {!optionsLoading && optionsError && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1 }}>
              <Typography sx={{ ...valueSx, fontSize: 12.5 }}>{optionsError}</Typography>
              <Button
                size="small"
                onClick={() => loadOptions(0)}
                sx={{ textTransform: "none" }}
              >
                Retry
              </Button>
            </Box>
          )}
          {!optionsLoading && !optionsError && total === 0 && (
            <Typography sx={{ ...valueSx, fontSize: 12.5, px: 2, py: 1.25 }}>
              {search ? `No warehouses match "${search}"` : "This client has no warehouses."}
            </Typography>
          )}
          {!optionsLoading && !optionsError && hasMore && (
            <Box sx={{ px: 1, py: 0.5 }}>
              <Button
                size="small"
                fullWidth
                onClick={() => loadOptions(options.length)}
                sx={{ textTransform: "none", fontFamily: "poppins", fontSize: 12.5 }}
              >
                Load more ({options.length} of {total})
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <SectionFooter>
        <Button
          onClick={cancel}
          disabled={saving}
          sx={{ ...settingsButtonSx, color: "var(--text-secondary)" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={save}
          disabled={saving || !dirty}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={settingsButtonSx}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </SectionFooter>
    </Box>
  );

  return (
    <SettingsSection
      testId="analytics-warehouses-setting"
      title={config?.label || "Analytics Warehouses"}
      description="Warehouses offered in the warehouse filter of analytics pages. Operational screens are not affected."
      action={
        !editing && (
          <SectionAction
            onClick={startEditing}
            disabled={loadingSaved || Boolean(savedError) || !clientId}
            aria-label="Edit analytics warehouses"
            startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
          >
            Edit
          </SectionAction>
        )
      }
    >
      {editing ? renderEditor() : renderView()}
    </SettingsSection>
  );
};

export default AnalyticsWarehousesSetting;
